'use client';

import { useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Typography,
    Badge,
    Divider,
    Card,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    InputBase,
    Stepper,
    Step,
    StepLabel,
    Fab,
} from '@mui/material';
import { Send, CheckCircle, AlertCircle, Play, MessageSquare, Calendar, DollarSign, Users } from 'lucide-react';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { DraggableBottomSheetModal } from '../Draggable/DraggableBottomSheet';

const steps = ['Brief Review', 'Content Creation', 'Review & Approval', 'Publication'];

const messages = [
    {
        id: 1,
        sender: 'Job Creator',
        avatar: 'JC',
        message: 'Hi! Thanks for accepting this collaboration...',
        time: '2:30 PM',
        isOwn: false,
    },
    {
        id: 2,
        sender: 'You',
        avatar: 'YU',
        message: 'Excited to work with you!...',
        time: '2:45 PM',
        isOwn: true,
    },
    {
        id: 3,
        sender: 'Job Creator',
        avatar: 'JC',
        message: "Perfect! We'd love to see the first draft...",
        time: '3:00 PM',
        isOwn: false,
    },
];

interface MessageBubbleProps {
    message: string;
    isOwn: boolean;
    avatar: string;
    time: string;
    themeStyle: string;
}

const MessageBubble = ({ message, isOwn, avatar, time }: MessageBubbleProps) => {
    const { themeStyle } = useThemeContext(); // fallback if needed
    const currentTheme = theme[themeStyle];

    return (
        <Box display="flex" flexDirection={isOwn ? 'row-reverse' : 'row'} alignItems="flex-start" gap={1} mb={1.5}>
            <Avatar
                sx={{
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: isOwn ? currentTheme.themePuprle : 'blue.100',
                    color: isOwn ? 'purple.600' : 'blue.600',
                }}>
                {avatar}
            </Avatar>

            <Box display="flex" flexDirection="column" alignItems={isOwn ? 'flex-end' : 'flex-start'}>
                <Box
                    px={1.5}
                    py={1}
                    borderRadius={2}
                    maxWidth={240}
                    bgcolor={isOwn ? currentTheme.themePuprle : 'grey.100'}
                    color={isOwn ? 'white' : 'text.primary'}>
                    <Typography variant="body2">{message}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: theme[themeStyle].tag_white, fontSize: '0.65rem', mt: 0.5 }}>
                    {time}
                </Typography>
            </Box>
        </Box>
    );
};

interface BulletListSectionProps {
    title: string;
    items: string[];
    textColor: string;
    bulletColor: string;
}

const BulletListSection = ({ title, items, textColor, bulletColor }: BulletListSectionProps) => (
    <Box>
        <Typography variant="subtitle1" fontWeight="medium" color={textColor} mb={1}>
            {title}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
            {items.map((item, index) => (
                <Box
                    component="li"
                    key={index}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1,
                    }}>
                    <Box
                        component="span"
                        sx={{
                            width: 6,
                            height: 6,
                            mt: '6px',
                            borderRadius: '50%',
                            backgroundColor: bulletColor,
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="body2" color={textColor}>
                        {item}
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

export const BuzzBridgeJobDetails = () => {
    const [newMessage, setNewMessage] = useState('');
    const [showChat, setShowChat] = useState(false);
    const { themeStyle, device } = useThemeContext();
    const currentTheme = theme[themeStyle];
    const textColor = currentTheme.tag_white;
    const cardBg = currentTheme.secondaryBg;

    const handleSendMessage = () => {
        if (newMessage.trim()) setNewMessage('');
    };

    const deliverables = ['3 Instagram posts', '5 Instagram stories', '1 Reel (30–60 seconds)'];

    const requirements = ['Use provided hashtags', 'Tag @fashionbrandco', 'Include product shots'];

    const mobileStyle = device.isMobile ? { position: 'absolute', bottom: 0, left: 0, width: '100%' } : {};

    const chatComp = (
        <Grid item xs={12} lg={4}>
            <Card
                elevation={3}
                sx={{
                    height: {
                        xs: '100dvh',
                        md: '70dvh',
                    },
                    maxHeight: '100dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: cardBg,
                }}>
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center" gap={1}>
                            <MessageSquare size={20} color="#2563eb" />
                            <Typography variant="subtitle1" color={textColor}>
                                Project Chat
                            </Typography>
                        </Box>
                    }
                    subheader={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Avatar
                                sx={{
                                    width: 16,
                                    height: 16,
                                    bgcolor: 'blue.100',
                                    color: 'blue.600',
                                    fontSize: '0.5rem',
                                }}>
                                JC
                            </Avatar>
                            <Typography variant="caption" sx={{ color: theme[themeStyle].tag_white }}>
                                Fashion Brand Co.
                            </Typography>
                            <Box flexGrow={1} />
                            <Box width={8} height={8} borderRadius="50%" bgcolor="success.main" />
                        </Box>
                    }
                />
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        p: 0,
                        minHeight: 0, // necessary for proper scroll behavior
                        '&:last-child': {
                            paddingBottom: 0,
                        },
                    }}>
                    {/* Chat Scrollable Area */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            p: 2,
                        }}>
                        {messages.map(msg => (
                            <MessageBubble
                                key={msg.id}
                                message={msg.message}
                                isOwn={msg.isOwn}
                                avatar={msg.avatar}
                                time={msg.time}
                                themeStyle={themeStyle}
                            />
                        ))}
                    </Box>

                    <Divider />

                    {/* Message Input (stuck at bottom) */}
                    <Box p={2} display="flex" gap={1} sx={mobileStyle}>
                        <InputBase
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
                            sx={{
                                flex: 1,
                                bgcolor: 'background.paper',
                                px: 1,
                                borderRadius: 1,
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                        <IconButton
                            color="primary"
                            onClick={handleSendMessage}
                            sx={{ backgroundColor: theme[themeStyle].themePuprle, borderRadius: 1 }}>
                            <Send size={18} color={theme[themeStyle].true_white} />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );

    const chat = !device.isMobile ? (
        chatComp
    ) : (
        <DraggableBottomSheetModal
            style={{ zIndex: 9999, position: 'fixed', marginTop: '20%' }}
            customHeight={'92dvh'}
            open={showChat}
            onDismiss={() => setShowChat(false)}>
            <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>{chatComp}</Box>
        </DraggableBottomSheetModal>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box maxWidth="lg" mx="auto">
                <Grid container spacing={3}>
                    {/* Main Content */}
                    <Grid item xs={12} lg={8} display="flex" flexDirection="column" gap={3}>
                        {/* Job Preview */}
                        <Card elevation={3} sx={{ backgroundColor: cardBg }}>
                            <CardHeader
                                titleTypographyProps={{ variant: 'h5', fontWeight: 'bold', color: textColor }}
                                title="Summer Fashion Campaign"
                                action={
                                    <Badge
                                        color="success"
                                        badgeContent="Active"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.7rem',
                                                right: 25, // adjust as needed
                                                top: 8,
                                            },
                                        }}
                                    />
                                }
                            />
                            <CardContent>
                                <Box display="flex" gap={4} mb={2} color={textColor}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Calendar size={16} /> Due: June 25, 2024
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <DollarSign size={16} /> $2,500
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Users size={16} /> Fashion Brand Co.
                                    </Box>
                                </Box>
                                <Box
                                    mb={2}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius={2}
                                    height={{
                                        xs: 200,
                                        sm: 300,
                                    }}
                                    sx={{
                                        background: 'linear-gradient(to bottom right, #F3E8FF, #FCE7F3)',
                                    }}>
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        width={64}
                                        height={64}
                                        bgcolor="white"
                                        borderRadius="50%"
                                        boxShadow={2}
                                        mb={2}>
                                        <Play size={32} style={{ color: '#9333ea' }} />
                                    </Box>
                                    <Typography variant="subtitle2" color={textColor}>
                                        Campaign Preview
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Click to view mood board & assets
                                    </Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    {['Asset 1', 'Asset 2', 'Asset 3'].map((asset, i) => (
                                        <Grid item xs={4} key={i}>
                                            <Box
                                                height={100}
                                                bgcolor="grey.100"
                                                borderRadius={2}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center">
                                                <Typography variant="caption" color="text.secondary">
                                                    {asset}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Project Timeline with Stepper */}
                        <Card elevation={3} sx={{ backgroundColor: cardBg }}>
                            <CardHeader
                                title={
                                    <Typography variant="h6" color={textColor}>
                                        Project Timeline
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Stepper activeStep={1} alternativeLabel>
                                    {steps.map(label => (
                                        <Step key={label}>
                                            <StepLabel
                                                StepIconProps={{
                                                    sx: {
                                                        color: currentTheme.tag_white,
                                                        '&.Mui-active': { color: '#2563eb' },
                                                        '&.Mui-completed': { color: '#16a34a' },
                                                    },
                                                }}>
                                                <Typography variant="caption" color={textColor}>
                                                    {label}
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </CardContent>
                        </Card>

                        {/* Job Details */}
                        <Card elevation={3} sx={{ backgroundColor: cardBg }}>
                            <CardHeader
                                title={
                                    <Typography variant="h6" color={textColor}>
                                        Job Details
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Box mb={2}>
                                    <Typography variant="subtitle1" fontWeight="medium" color={textColor}>
                                        Campaign Brief
                                    </Typography>
                                    <Typography variant="body2" color={textColor}>
                                        Create engaging content showcasing our new summer collection...
                                    </Typography>
                                </Box>
                                <Divider sx={{ borderColor: textColor }} />
                                <Grid container spacing={2} mt={2}>
                                    <Grid item xs={12} sm={6}>
                                        <BulletListSection
                                            title="Deliverables"
                                            items={deliverables}
                                            textColor={textColor}
                                            bulletColor={theme[themeStyle].tag_white}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <BulletListSection
                                            title="Requirements"
                                            items={requirements}
                                            textColor={textColor}
                                            bulletColor={theme[themeStyle].tag_white}
                                        />
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2, borderColor: textColor }} />
                                <Box display="flex" gap={2}>
                                    <Button
                                        sx={{ width: '100%', textTransform: 'capitalize' }}
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircle size={16} />}>
                                        Approve & Continue
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AlertCircle size={16} />}
                                        sx={{
                                            color: textColor,
                                            borderColor: textColor,
                                            width: '100%',
                                            textTransform: 'capitalize',
                                        }}>
                                        Request Changes
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sidebar Chat */}
                    {chat}
                    {/* FAB to open chat on mobile */}
                    {device.isMobile && (
                        <Fab
                            color="primary"
                            onClick={() => setShowChat(true)}
                            sx={{
                                position: 'fixed',
                                bottom: 24,
                                right: 24,
                                zIndex: 2000,
                                backgroundColor: theme[themeStyle].themePuprle,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].themePuprle,
                                    opacity: 0.85,
                                },
                            }}>
                            <MessageSquare size={20} color={theme[themeStyle].true_white} />
                        </Fab>
                    )}
                </Grid>
            </Box>
        </Box>
    );
};
